/**
 * Mode-2 (external / physical tanpura) informed separation math (ADR-0003, FR-7).
 *
 * The drone fundamental (Sa) and its first string (Pa or Ma) are known, so this is
 * informed cancellation, not blind separation:
 *   - comb-notch frequencies at the known drone pitches + harmonics (time-domain
 *     BiquadFilter chain lives in the web engine);
 *   - a calibration template (averaged drone-only spectrum) + spectral subtraction;
 *   - a residual-energy presence gate to reject drone-only frames (AC-7.3).
 *
 * All pure and unit-tested; the browser wires these into the audio graph.
 */
import { magnitudeSpectrum } from './fft.js';
/**
 * Frequencies to notch for a given Sa. Includes Sa (and Pa=3/2 or Ma=4/3) and their
 * harmonics up to the Nyquist frequency. De-duplicated and ascending.
 */
export function combNotchFrequencies(saHz, nyquistHz, options = {}) {
    const harmonics = options.harmonics ?? 6;
    const firstString = options.firstString ?? 'Pa';
    const bases = [saHz];
    if (firstString === 'Pa')
        bases.push(saHz * (3 / 2));
    else if (firstString === 'Ma')
        bases.push(saHz * (4 / 3));
    const freqs = new Set();
    for (const base of bases) {
        for (let h = 1; h <= harmonics; h++) {
            const f = base * h;
            if (f > 0 && f < nyquistHz)
                freqs.add(Math.round(f * 1000) / 1000);
        }
    }
    return [...freqs].sort((a, b) => a - b);
}
/** Average the magnitude spectra of drone-only frames into a calibration template. */
export function buildDroneTemplate(frames) {
    if (frames.length === 0)
        throw new Error('buildDroneTemplate: no frames');
    const spectra = frames.map((f) => magnitudeSpectrum(f));
    const bins = spectra[0].length;
    const template = new Float32Array(bins);
    for (const s of spectra) {
        if (s.length !== bins)
            throw new Error('buildDroneTemplate: inconsistent frame sizes');
        for (let k = 0; k < bins; k++)
            template[k] += s[k];
    }
    for (let k = 0; k < bins; k++)
        template[k] /= spectra.length;
    return template;
}
/**
 * Spectral subtraction: remove the drone template from a live magnitude spectrum.
 * A spectral floor (fraction of the original) avoids negative/zero artifacts.
 */
export function spectralSubtract(mag, template, floorFactor = 0) {
    if (mag.length !== template.length)
        throw new Error('spectralSubtract: size mismatch');
    const out = new Float32Array(mag.length);
    for (let k = 0; k < mag.length; k++) {
        out[k] = Math.max(mag[k] - template[k], floorFactor * mag[k]);
    }
    return out;
}
/** Fraction of spectral energy that remains after subtracting the drone template. */
export function residualEnergyRatio(mag, template) {
    const residual = spectralSubtract(mag, template);
    let resE = 0;
    let magE = 0;
    for (let k = 0; k < mag.length; k++) {
        resE += residual[k] * residual[k];
        magE += mag[k] * mag[k];
    }
    return magE > 0 ? resE / magE : 0;
}
/**
 * Presence gate: true when the frame carries energy beyond the drone template
 * (i.e. the practitioner is sounding). Below threshold → drone-only → report no input.
 */
export function isVoicePresent(frame, template, threshold = 0.1) {
    return residualEnergyRatio(magnitudeSpectrum(frame), template) > threshold;
}
//# sourceMappingURL=drone.js.map